import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * SlideViewer — renders the single Avneesh_PPT.pptx slide as an HTML replica.
 * Assets live in /slides/ (extracted from the PPTX media folder).
 *
 * Layout:
 *   - Dark blue gradient background (image1.png used as banner)
 *   - Title: "transform consumer experience @ AI"
 *   - AI animated GIFs (image2.gif, image3.gif)
 *   - Vi logo (image4.png)
 *   - Six agent cards in a grid
 *   - Subtitle text about predictive CX
 */

const AGENT_CARDS = [
  { title: "recharge & renewal assistant", subtitle: "" },
  { title: "comms manager", subtitle: "fully automated 1-1 comms" },
  { title: "smart plan advisor, sales agent", subtitle: "" },
  { title: "fraud & risk protection agent", subtitle: "" },
  { title: "insights manager", subtitle: "automated reports & analytics" },
  {
    title: "social keeper",
    subtitle: "scan & flag potential concerns in real time",
  },
];

const SlideViewer = ({ phase }) => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        borderRadius: "12px",
        overflow: "hidden",
        position: "relative",
        background: "linear-gradient(180deg, #0a1628 0%, #0d1f3c 50%, #091422 100%)",
      }}
    >
      {/* Background banner image (cropped top strip) */}
      <Box
        component="img"
        src="/slides/image1.png"
        alt=""
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: 0.35,
          pointerEvents: "none",
        }}
      />

      {/* Vi logo — top right */}
      <Box
        component="img"
        src="/slides/image4.png"
        alt="Vi logo"
        sx={{
          position: "absolute",
          top: 8,
          right: 12,
          height: { xs: "28px", md: "40px" },
          objectFit: "contain",
          zIndex: 2,
        }}
      />

      {/* Content layer */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Top row: Title + AI GIF */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          {/* Title */}
          <Box sx={{ flex: 1, pr: 2 }}>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: { xs: "1.3rem", md: "1.8rem" },
                lineHeight: 1.2,
                textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
              }}
            >
              transform consumer experience
            </Typography>
            <Typography
              sx={{
                color: "#FF0000",
                fontWeight: 800,
                fontSize: { xs: "1.6rem", md: "2.2rem" },
                lineHeight: 1.2,
                textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
              }}
            >
              @ AI
            </Typography>
          </Box>

          {/* AI animated GIF — top right area */}
          <Box
            component="img"
            src="/slides/image2.gif"
            alt="AI Technology"
            sx={{
              width: { xs: "100px", md: "160px" },
              height: { xs: "75px", md: "120px" },
              objectFit: "cover",
              borderRadius: "8px",
              flexShrink: 0,
            }}
          />
        </Box>

        {/* Middle row: AI illustration GIF + Agent cards + Subtitle */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1.5, md: 2 },
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Left: AI illustration GIF */}
          <Box
            component="img"
            src="/slides/image3.gif"
            alt="AI illustration"
            sx={{
              width: { xs: "90px", md: "150px" },
              height: { xs: "70px", md: "110px" },
              objectFit: "cover",
              borderRadius: "8px",
              alignSelf: "center",
              flexShrink: 0,
            }}
          />

          {/* Center: Agent cards grid */}
          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: { xs: 0.8, md: 1 },
              alignContent: "center",
            }}
          >
            {AGENT_CARDS.map((card) => (
              <Box
                key={card.title}
                sx={{
                  backgroundColor: "rgba(20, 30, 60, 0.6)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "6px",
                  px: { xs: 1, md: 1.5 },
                  py: { xs: 0.6, md: 0.8 },
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: { xs: "0.6rem", md: "0.8rem" },
                    lineHeight: 1.3,
                    textShadow: "1px 1px 4px rgba(0,0,0,0.5)",
                  }}
                >
                  {card.title}
                </Typography>
                {card.subtitle && (
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      fontSize: { xs: "0.5rem", md: "0.65rem" },
                      lineHeight: 1.2,
                      mt: 0.3,
                    }}
                  >
                    {card.subtitle}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* Right: Subtitle text */}
          <Box
            sx={{
              maxWidth: { xs: "90px", md: "140px" },
              alignSelf: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: "#0070C0",
                fontWeight: 600,
                fontSize: { xs: "0.55rem", md: "0.75rem" },
                lineHeight: 1.4,
              }}
            >
              from reactive service to predictive, personalized & autonomous
              customer experience
            </Typography>
          </Box>
        </Box>

        {/* Phase indicator at bottom */}
        {phase && (
          <Box
            sx={{
              mt: 1.5,
              textAlign: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.7rem",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              {phase}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SlideViewer;
